import React, { useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Crown,
  RefreshCw,
  Search,
  ShieldAlert,
  ShieldCheck,
  Trash2,
  UserCog,
  Users,
} from "lucide-react";

import {
  AdminRole,
  AdminUser,
  adminUserRoleOptions,
  deleteAdminUser,
  getAdminUsers,
  normalizeLegacyAdminRoles,
  updateAdminUserRole,
} from "../api/adminUsers";
import { useAppSelector } from "../hooks/reducer";

const roleLabels = adminUserRoleOptions.reduce<Record<string, string>>(
  (acc, item) => {
    acc[item.value] = item.label;
    return acc;
  },
  { admin: "Старий адмін" }
);

const adminRoleSet = new Set([
  "admin",
  "super_admin",
  "education_admin",
  "career_manager",
]);

const AdminUsersPage = () => {
  const currentUser = useAppSelector((state) => state.authReducer.data?.user);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingUserId, setSavingUserId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null);
  const [serviceActionsOpen, setServiceActionsOpen] = useState(false);
  const [normalizeConfirmOpen, setNormalizeConfirmOpen] = useState(false);

  const loadUsers = async () => {
    setLoading(true);
    setError("");

    try {
      const data = await getAdminUsers();
      setUsers(data);
    } catch (err: any) {
      setError(err?.response?.data?.message || "Не вдалося завантажити користувачів.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const filteredUsers = useMemo(() => {
    const search = query.trim().toLowerCase();

    return users.filter((user) => {
      const role = user.role || "participant";
      const matchesRole = roleFilter === "all" || role === roleFilter;
      const matchesSearch =
        !search ||
        [user.username, user.email, user.telephone]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(search));

      return matchesRole && matchesSearch;
    });
  }, [query, roleFilter, users]);

  const stats = useMemo(() => {
    const total = users.length;
    const admins = users.filter((user) => adminRoleSet.has(user.role || "")).length;
    const candidates = users.filter((user) => user.role === "candidate").length;
    const participants = users.filter((user) => user.role === "participant").length;

    return { total, admins, candidates, participants };
  }, [users]);

  const handleRoleChange = async (userId: string, role: AdminRole) => {
    setSavingUserId(userId);
    setMessage("");
    setError("");

    try {
      const updatedUser = await updateAdminUserRole(userId, role);
      setUsers((prev) =>
        prev.map((user) => (user._id === userId ? { ...user, ...updatedUser } : user))
      );
      setMessage("Роль користувача оновлено.");
    } catch (err: any) {
      setError(err?.response?.data?.message || "Не вдалося оновити роль.");
    } finally {
      setSavingUserId(null);
    }
  };

  const handleNormalizeAdmins = async () => {
    setSavingUserId("normalize");
    setMessage("");
    setError("");

    try {
      const result = await normalizeLegacyAdminRoles();
      setMessage(
        `Синхронізовано: ${result.updated}. Супер-адмінів: ${result.superAdmins}, освітніх адмінів: ${result.educationAdmins}.`
      );
      await loadUsers();
      setNormalizeConfirmOpen(false);
    } catch (err: any) {
      setError(err?.response?.data?.message || "Не вдалося синхронізувати ролі.");
    } finally {
      setSavingUserId(null);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteTarget) return;

    setSavingUserId(`delete:${deleteTarget._id}`);
    setMessage("");
    setError("");

    try {
      await deleteAdminUser(deleteTarget._id);
      setUsers((prev) => prev.filter((user) => user._id !== deleteTarget._id));
      setMessage("Акаунт користувача видалено.");
      setDeleteTarget(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || "Не вдалося видалити акаунт.");
    } finally {
      setSavingUserId(null);
    }
  };

  return (
    <section className="min-h-full bg-[#f4f7fb] px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1500px]">
        <div className="border-b border-black/10 pb-6">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.18em] text-[#8a6f00]">
              Супер-адмін
            </p>
            <h1 className="mt-1 text-[34px] font-black uppercase leading-none tracking-[-0.04em] text-black sm:text-[46px]">
              Користувачі й ролі
            </h1>
            <p className="mt-3 max-w-[760px] text-sm font-semibold leading-6 text-slate-600">
              Тут можна переглянути всіх зареєстрованих людей на платформі та
              призначити їм потрібну роль доступу.
            </p>
          </div>
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-4">
          {[
            { label: "Усього людей", value: stats.total, icon: Users },
            { label: "Адмінські ролі", value: stats.admins, icon: ShieldCheck },
            { label: "Кандидати", value: stats.candidates, icon: UserCog },
            { label: "Учасники", value: stats.participants, icon: Crown },
          ].map(({ label, value, icon: Icon }) => (
            <div
              key={label}
              className="rounded-[22px] border border-slate-200 bg-white p-5 shadow-[0_16px_35px_rgba(15,23,42,0.06)]"
            >
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.12em] text-[#8a6f00]">
                    {label}
                  </p>
                  <p className="mt-2 text-3xl font-black text-black">{value}</p>
                </div>
                <span className="grid h-12 w-12 place-items-center rounded-[16px] border-2 border-black bg-[#ffd400] text-black shadow-[3px_3px_0_#000]">
                  <Icon size={21} />
                </span>
              </div>
            </div>
          ))}
        </div>

        {(message || error) && (
          <div
            className={`mt-5 flex items-center gap-3 rounded-[16px] border px-4 py-3 text-sm font-bold ${
              error
                ? "border-red-200 bg-red-50 text-red-700"
                : "border-amber-200 bg-[#fff8cf] text-black"
            }`}
          >
            <AlertCircle size={18} />
            {error || message}
          </div>
        )}

        <div className="mt-6 rounded-[26px] border border-slate-200 bg-white shadow-[0_18px_45px_rgba(15,23,42,0.08)]">
          <div className="grid gap-3 border-b border-slate-200 p-4 md:grid-cols-[1fr_260px]">
            <label className="relative block">
              <Search
                size={18}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Пошук за ім'ям, email або телефоном"
                className="h-12 w-full rounded-[16px] border border-slate-200 bg-white pl-11 pr-4 text-sm font-bold outline-none transition focus:border-[#ffd400] focus:ring-4 focus:ring-[#ffd400]/20"
              />
            </label>

            <select
              value={roleFilter}
              onChange={(event) => setRoleFilter(event.target.value)}
              className="h-12 rounded-[16px] border border-slate-200 bg-white px-4 text-sm font-bold outline-none transition focus:border-[#ffd400] focus:ring-4 focus:ring-[#ffd400]/20"
            >
              <option value="all">Усі ролі</option>
              {adminUserRoleOptions.map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
              <option value="admin">Старий адмін</option>
            </select>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-[980px] w-full border-collapse">
              <thead>
                <tr className="bg-[#fffdf2] text-left text-[11px] font-black uppercase tracking-[0.12em] text-[#8a6f00]">
                  <th className="px-5 py-4">Користувач</th>
                  <th className="px-5 py-4">Контакти</th>
                  <th className="px-5 py-4">Поточна роль</th>
                  <th className="px-5 py-4">Доступ</th>
                  <th className="px-5 py-4">Змінити роль</th>
                  <th className="px-5 py-4">Дії</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-5 py-10 text-center text-sm font-bold text-slate-500">
                      Завантаження користувачів...
                    </td>
                  </tr>
                ) : filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-5 py-10 text-center text-sm font-bold text-slate-500">
                      Нічого не знайдено.
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => {
                    const role = user.role || "participant";
                    const isSelf = user._id === currentUser?._id;
                    const isSaving = savingUserId === user._id;
                    const isDeleting = savingUserId === `delete:${user._id}`;
                    const cannotDelete = isSelf || role === "super_admin";

                    return (
                      <tr key={user._id} className="align-top">
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <div className="grid h-11 w-11 shrink-0 place-items-center rounded-[14px] border-2 border-black bg-[#ffd400] text-sm font-black shadow-[3px_3px_0_#000]">
                              {(user.username || user.email || "U")
                                .split(" ")
                                .map((part) => part[0])
                                .join("")
                                .slice(0, 2)
                                .toUpperCase()}
                            </div>
                            <div>
                              <p className="font-black text-black">{user.username || "Без імені"}</p>
                              <p className="mt-1 text-xs font-semibold text-slate-500">
                                ID: {user._id}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <p className="text-sm font-bold text-black">{user.telephone || "-"}</p>
                          <p className="mt-1 text-sm font-semibold text-slate-500">
                            {user.email || "email не вказано"}
                          </p>
                        </td>
                        <td className="px-5 py-4">
                          <span className="inline-flex rounded-full bg-[#fff3a3] px-3 py-1 text-xs font-black uppercase text-black">
                            {roleLabels[role] || role}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <p className="text-sm font-black text-black">
                            {user.isAdmin ? "Адмінський" : "Звичайний"}
                          </p>
                          <p className="mt-1 text-xs font-semibold text-slate-500">
                            Програма: {user.programAccess || "-"}
                          </p>
                        </td>
                        <td className="px-5 py-4">
                          <select
                            value={role === "admin" ? "education_admin" : role}
                            disabled={isSaving || (isSelf && role === "super_admin")}
                            onChange={(event) =>
                              handleRoleChange(user._id, event.target.value as AdminRole)
                            }
                            className="h-11 min-w-[220px] rounded-[14px] border border-slate-200 bg-white px-3 text-sm font-bold outline-none transition focus:border-[#ffd400] focus:ring-4 focus:ring-[#ffd400]/20 disabled:cursor-not-allowed disabled:bg-slate-100"
                          >
                            {adminUserRoleOptions.map((roleOption) => (
                              <option key={roleOption.value} value={roleOption.value}>
                                {roleOption.label}
                              </option>
                            ))}
                          </select>
                          {isSelf && role === "super_admin" && (
                            <p className="mt-2 text-xs font-bold text-slate-500">
                              Свій супер-доступ тут не знімаємо.
                            </p>
                          )}
                        </td>
                        <td className="px-5 py-4">
                          <button
                            type="button"
                            disabled={cannotDelete || isDeleting}
                            onClick={() => setDeleteTarget(user)}
                            className="inline-flex h-11 items-center justify-center gap-2 rounded-[14px] border border-red-200 bg-red-50 px-4 text-sm font-black text-red-700 transition hover:-translate-y-0.5 hover:border-red-300 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 disabled:hover:translate-y-0"
                          >
                            <Trash2 size={17} />
                            {isDeleting ? "Видаляю..." : "Видалити"}
                          </button>
                          {cannotDelete && (
                            <p className="mt-2 max-w-[180px] text-xs font-bold text-slate-500">
                              {isSelf
                                ? "Свій акаунт видалити не можна."
                                : "Супер-адміна не видаляємо з таблиці."}
                            </p>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-6 rounded-[22px] border border-slate-200 bg-white shadow-[0_14px_35px_rgba(15,23,42,0.06)]">
          <button
            type="button"
            onClick={() => setServiceActionsOpen((value) => !value)}
            className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
          >
            <div className="flex items-center gap-3">
              <span className="grid h-11 w-11 place-items-center rounded-[14px] border border-amber-200 bg-[#fff8cf] text-[#8a6f00]">
                <ShieldAlert size={20} />
              </span>
              <div>
                <p className="text-sm font-black uppercase tracking-[0.08em] text-black">
                  Службові дії
                </p>
                <p className="mt-1 text-xs font-semibold text-slate-500">
                  Рідкісні операції для міграції ролей та обслуговування доступів.
                </p>
              </div>
            </div>
            {serviceActionsOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>

          {serviceActionsOpen && (
            <div className="border-t border-slate-100 p-5">
              <div className="rounded-[18px] border border-amber-200 bg-[#fffdf2] p-4">
                <p className="text-sm font-black text-black">
                  Синхронізація старих адмінів
                </p>
                <p className="mt-2 text-sm font-semibold leading-6 text-slate-600">
                  Використовуй цю дію тільки після додавання
                  `SUPER_ADMIN_EMAILS` або `SUPER_ADMIN_TELEPHONES` на бекенді.
                  Вона перетворює старих `admin` / `isAdmin: true` у нові ролі:
                  твій акаунт стане `super_admin`, інші старі адміни -
                  `education_admin`.
                </p>
                <button
                  type="button"
                  onClick={() => setNormalizeConfirmOpen(true)}
                  disabled={savingUserId === "normalize"}
                  className="mt-4 inline-flex items-center justify-center gap-3 rounded-[14px] border-2 border-black bg-white px-4 py-3 text-sm font-black text-black shadow-[3px_3px_0_#ffd400] transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <RefreshCw
                    size={18}
                    className={savingUserId === "normalize" ? "animate-spin" : ""}
                  />
                  Підготувати синхронізацію
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {normalizeConfirmOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/55 p-4">
          <div className="w-full max-w-xl overflow-hidden rounded-[26px] border-2 border-black bg-white shadow-[8px_8px_0_#ffd400]">
            <div className="border-b-2 border-black bg-[#fffdf2] p-5">
              <div className="flex items-center gap-3">
                <span className="grid h-12 w-12 place-items-center rounded-[16px] border-2 border-black bg-[#ffd400] text-black shadow-[3px_3px_0_#000]">
                  <ShieldAlert size={22} />
                </span>
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.14em] text-[#8a6f00]">
                    Увага
                  </p>
                  <h2 className="text-2xl font-black uppercase leading-none text-black">
                    Підтвердити синхронізацію?
                  </h2>
                </div>
              </div>
            </div>

            <div className="space-y-4 p-5 text-sm font-semibold leading-6 text-slate-700">
              <p>
                Цю кнопку потрібно натискати тільки один раз після налаштування
                `SUPER_ADMIN_EMAILS` / `SUPER_ADMIN_TELEPHONES` і перезапуску
                бекенда.
              </p>
              <p>
                Після запуску всі старі адміни будуть переведені в нову систему
                ролей: супер-адмін визначиться по email або телефону з env, а
                решта старих адмінів стануть освітніми адмінами.
              </p>
              <div className="rounded-[16px] border border-red-200 bg-red-50 p-4 font-bold text-red-700">
                Не натискай це випадково під час звичайного редагування ролей.
              </div>
            </div>

            <div className="flex flex-col gap-3 border-t border-slate-200 bg-slate-50 p-5 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setNormalizeConfirmOpen(false)}
                disabled={savingUserId === "normalize"}
                className="rounded-[14px] border border-slate-200 bg-white px-5 py-3 text-sm font-black text-black"
              >
                Скасувати
              </button>
              <button
                type="button"
                onClick={handleNormalizeAdmins}
                disabled={savingUserId === "normalize"}
                className="inline-flex items-center justify-center gap-3 rounded-[14px] border-2 border-black bg-[#ffd400] px-5 py-3 text-sm font-black text-black shadow-[3px_3px_0_#000] disabled:cursor-not-allowed disabled:opacity-60"
              >
                <RefreshCw
                  size={18}
                  className={savingUserId === "normalize" ? "animate-spin" : ""}
                />
                Так, синхронізувати
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/55 p-4">
          <div className="w-full max-w-xl overflow-hidden rounded-[26px] border-2 border-black bg-white shadow-[8px_8px_0_#ffd400]">
            <div className="border-b-2 border-black bg-red-50 p-5">
              <div className="flex items-center gap-3">
                <span className="grid h-12 w-12 place-items-center rounded-[16px] border-2 border-black bg-[#ffd400] text-black shadow-[3px_3px_0_#000]">
                  <Trash2 size={22} />
                </span>
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.14em] text-red-700">
                    Небезпечна дія
                  </p>
                  <h2 className="text-2xl font-black uppercase leading-none text-black">
                    Видалити акаунт?
                  </h2>
                </div>
              </div>
            </div>

            <div className="space-y-4 p-5 text-sm font-semibold leading-6 text-slate-700">
              <p>
                Буде видалено акаунт користувача{" "}
                <span className="font-black text-black">
                  {deleteTarget.username || deleteTarget.email || deleteTarget.telephone}
                </span>
                .
              </p>
              <p>
                Також система прибере його заявки, прогрес у самостійних курсах,
                здачі завдань, відвідуваність і привʼязку до потоків та
                персональних активностей.
              </p>
              <div className="rounded-[16px] border border-red-200 bg-red-50 p-4 font-bold text-red-700">
                Дію не можна швидко відкотити. Натискай тільки якщо акаунт
                справді потрібно прибрати.
              </div>
            </div>

            <div className="flex flex-col gap-3 border-t border-slate-200 bg-slate-50 p-5 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                disabled={savingUserId === `delete:${deleteTarget._id}`}
                className="rounded-[14px] border border-slate-200 bg-white px-5 py-3 text-sm font-black text-black"
              >
                Скасувати
              </button>
              <button
                type="button"
                onClick={handleDeleteUser}
                disabled={savingUserId === `delete:${deleteTarget._id}`}
                className="inline-flex items-center justify-center gap-3 rounded-[14px] border-2 border-black bg-red-600 px-5 py-3 text-sm font-black text-white shadow-[3px_3px_0_#000] disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Trash2 size={18} />
                {savingUserId === `delete:${deleteTarget._id}`
                  ? "Видаляю..."
                  : "Так, видалити"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default AdminUsersPage;
